import {AlertIcon, GearIcon} from '@primer/octicons-react'
import {Box, Button, type ButtonProps, Heading, type SxProp, Tooltip} from '@primer/react'
import {clsx} from 'clsx'
import {forwardRef} from 'react'

import {LABELS} from './fields/constants/labels'
import styles from './SectionHeader.module.css'

export type StyledHeadingProps = {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
} & SxProp

export type SectionHeaderProps = {
  /**
   * Section heading id
   */
  id?: string
  title: string
  buttonProps?: ButtonProps
  headingProps?: StyledHeadingProps
  readonly?: boolean
  errorPresent?: boolean
} & SxProp

export const SectionHeader = forwardRef<HTMLButtonElement, SectionHeaderProps>(
  (
    {
      id,
      title,
      buttonProps,
      // eslint-disable-next-line @eslint-react/no-unstable-default-props
      headingProps: {as: Component = 'h3', ...headingProps} = {},
      readonly,
      errorPresent,
      ...props
    }: SectionHeaderProps,
    ref,
  ) => {
    const containerProps = readonly
      ? {
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          pb: '8px',
          ...props.sx,
        }
      : {
          width: '100%',
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          ...props.sx,
        }

    const content = (
      <Box sx={containerProps}>
        <Heading
          id={id}
          as={Component}
          sx={{
            position: readonly ? 'relative' : 'absolute',
            ...headingProps,
          }}
          className={styles.SectionHeaderTitle}
        >
          {title}
        </Heading>

        {!readonly && (
          <Button
            ref={ref}
            {...buttonProps}
            variant="invisible"
            size="small"
            trailingAction={() => (errorPresent ? <AlertIcon className="fgColor-danger" /> : <GearIcon />)}
            block
            className={styles.editButton}
            aria-describedby={errorPresent ? `error-message-${id}` : undefined}
          >
            <span className={clsx('sr-only', styles.screenReaderText)}>{LABELS.editFieldValueLabel(title)}</span>
            {errorPresent && (
              <span id={`error-message-${id}`} className="sr-only">
                {LABELS.errorUpdatingIssueField}
              </span>
            )}
          </Button>
        )}
      </Box>
    )

    // Wrap in tooltip if error is present, otherwise return as is
    return errorPresent && !readonly ? <Tooltip text={LABELS.errorUpdatingIssueField}>{content}</Tooltip> : content
  },
)
SectionHeader.displayName = 'SectionHeader'
